"use client";

import React, { useState } from "react";
import { useApp } from "./AppProvider";
import type { ContactWidgetData } from "@/lib/cms";

export default function ContactWidget({ data }: { data: ContactWidgetData }) {
  const { isRTL } = useApp();
  const [isOpen, setIsOpen] = useState(false);

  if (!data.isVisible) return null;

  const toggleOpen = () => setIsOpen(!isOpen);

  const whatsappLink = `https://wa.me/${data.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(
    isRTL ? data.whatsapp_message_ar : data.whatsapp_message_en
  )}`;

  const buttonStyle: React.CSSProperties = {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)",
    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    border: "none",
    padding: 0,
    outline: "none",
    position: "relative",
    zIndex: 110,
  };

  const itemStyle = (index: number): React.CSSProperties => ({
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    position: "absolute",
    bottom: isOpen ? `${70 + index * 60}px` : "0px",
    left: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 8px 20px -5px rgba(0, 0, 0, 0.25)",
    transition: "all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? "scale(1)" : "scale(0.5)",
    zIndex: 105 - index,
    border: "none",
    textDecoration: "none",
    visibility: isOpen ? "visible" : "hidden",
  });

  return (
    <div
      style={{
        position: "fixed",
        bottom: "30px",
        [isRTL ? "left" : "right"]: "30px",
        zIndex: 1000,
      }}
    >
      {/* WhatsApp */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        style={{ ...itemStyle(0), background: "#25D366" }}
        title={isRTL ? "واتساب" : "WhatsApp"}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 1 1-7.6-7.6 8.38 8.38 0 0 1 3.8.9L21 3.5z" />
        </svg>
      </a>

      {/* Phone */}
      <a
        href={`tel:${data.phone}`}
        style={{ ...itemStyle(1), background: "#3b82f6" }}
        title={isRTL ? "اتصال" : "Call"}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      </a>

      {/* Email */}
      <a
        href={`mailto:${data.email}`}
        style={{ ...itemStyle(2), background: "#ef4444" }}
        title={isRTL ? "بريد إلكتروني" : "Email"}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      </a>

      {/* Main Toggle Button */}
      <button
        onClick={toggleOpen}
        style={{
          ...buttonStyle,
          background: "var(--gradient-brand)",
          transform: isOpen ? "rotate(135deg)" : "rotate(0deg)",
        }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          {isOpen ? (
            <line x1="12" y1="5" x2="12" y2="19" />
          ) : (
            <>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <path d="M8 9h8" />
              <path d="M8 13h6" />
            </>
          )}
          {isOpen && <line x1="5" y1="12" x2="19" y2="12" />}
        </svg>
      </button>

      {/* Overlay to close when clicking outside */}
      {isOpen && (
        <div 
          onClick={toggleOpen} 
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 100 }} 
        />
      )}
    </div>
  );
}
